import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { CHECKOUT_SUBPATH } from './routing';

// When state machine changes the step, update URL to match
// This is very important, without changing the URL the new props for the page cannot be fetched.
export const useSyncPathToMachine = (
  routeChanging,
  isAppSession,
  stateKey,
  unmappedStates,
  pet_id,
  setShowPlanSelection,
) => {
  const router = useRouter();
  const [subpath, slug] = router.query.path ?? [];
  const pathname = slug ? `${subpath}/${slug}` : subpath;

  const syncPathToMachine = useCallback(() => {
    if (!routeChanging.current) {
      const query = isAppSession ? { isAppSession } : {};

      // Prevent duplicate navigation: if user is already on any path and we're attempting
      // to re-navigate to the same path, we should skip the navigation.
      // We check router.asPath to detect the current URL, but only skip navigation if
      // pathname is also undefined or equal to stateKey. This ensures we allow navigation
      // between /onboarding/ and /onboarding-v2/ while preventing duplicate navigation.
      // This fixes an issue where duplicate navigation attempts occur across multiple
      // See ticket: https://ollie.atlassian.net/browse/ES-1679
      const currentPath =
        router.asPath?.split('?')[0]?.replace(/\/$/, '') || '';
      const targetPath = stateKey ? `/${stateKey}` : '';
      const asPathMatchesTarget =
        targetPath && currentPath.endsWith(targetPath);
      const isAlreadyOnTargetPath =
        asPathMatchesTarget && (pathname === stateKey || !pathname);

      // Skip navigation if already on the target path
      if (isAlreadyOnTargetPath) {
        return;
      }
      if (stateKey && stateKey === CHECKOUT_SUBPATH && pathname !== stateKey)
        router.push({
          pathname: `${stateKey}`,
          query,
        });
      else if (
        stateKey &&
        pathname !== stateKey &&
        !unmappedStates.includes(stateKey)
      ) {
        query.pet_id = pet_id;
        router.push({
          pathname: `${stateKey}`,
          query,
        });
      }
    }
  }, [
    pathname,
    router,
    isAppSession,
    pet_id,
    routeChanging,
    stateKey,
    unmappedStates,
  ]);

  useEffect(() => {
    syncPathToMachine();
    const onRouteChangeComplete = () => {
      routeChanging.current = false;
      syncPathToMachine();
      setShowPlanSelection(false);
    };

    router.events.on('routeChangeComplete', onRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete);
    };
  }, [syncPathToMachine, router, routeChanging, setShowPlanSelection]);
};
